import { FilmIcon } from '@heroicons/react/24/outline';
import { Box, Flex, Progress, Space, Text, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { PageLoading } from '@/pages/PageLoading';
import type { ClubVideoFormKey } from '@/pages/club/onboard/components/stages/video/hooks/useVideoPage';
import { translate } from '@/shared/i18n/translate';
import { LBModalLayout } from '@/shared/layouts';
import { LBVideoDropZone } from '@/shared/modules';
import { LBButton } from '@/shared/ui-kit/components';

import { useVideoPage } from './hooks/useVideoPage';

export const Video = () => {
  const {
    state: { isLoading, form, isValidForm, progress },
    functions: { onSubmit, onDeleteVideo }
  } = useVideoPage();

  const [opened, { open, close }] = useDisclosure();
  const size = import.meta.env.VITE_VIDEO_MAX_SIZE as string;

  if (isLoading) return <PageLoading />;
  return (
    <>
      <LBModalLayout opened={opened} onClose={close}>
        <Title order={2}>{translate('popups.updateVideo.title')}</Title>
        <Space h={10} />
        <Text>{translate('popups.updateVideo.description')}</Text>
        <Space h={20} />
        <Box
          style={{
            borderRadius: 8,
            backgroundColor: 'white',
            zIndex: 99
          }}
        >
          <Progress value={progress} w='100%' />
        </Box>
      </LBModalLayout>
      <form onSubmit={onSubmit}>
        <Title order={1}>
          {translate('pages.onboarding.club.video.title')}
        </Title>
        <Space h={8} />
        <Text fz={14}>
          {translate('pages.onboarding.club.video.description')}
        </Text>
        <Space h={32} />
        {Object.entries(form.getValues()).map(([key, value], index) => (
          <div key={key}>
            <Text fz={14}>
              {index + 1}. {value.title}
            </Text>
            <Space h={4} />
            <LBVideoDropZone
              loading={form.submitting}
              maxFiles={1}
              initialValue={
                form.getValues()[key as ClubVideoFormKey].file !== null
                  ? [`${form.getValues()[key as ClubVideoFormKey].file}`]
                  : []
              }
              thumbnailPath={
                form.getValues()[key as ClubVideoFormKey].thumbnailPath || ''
              }
              onDelete={() => onDeleteVideo(value.id, value.tag)}
              onFilesChange={(file) => {
                form.setFieldValue(`${key}.file`, file[0]);
              }}
            >
              <Flex align='center' direction='column' py={70}>
                <FilmIcon color='gray' width={64} />
                <Text c='blue' fw={700} fz={16}>
                  {translate('commons.uploadFromDevice')}
                </Text>{' '}
                <Text fz={12} ta='center'>
                  {translate('commons.videoParams', { size })}
                </Text>
              </Flex>
            </LBVideoDropZone>
            <Space h={16} />
          </div>
        ))}

        <Space h={32} />
        {form.submitting && (
          <Box
            style={{
              borderRadius: 8,
              backgroundColor: 'white',
              zIndex: 99,
              transform: 'translateY(-50%)'
            }}
          >
            <Progress value={progress} w='100%' />
          </Box>
        )}
        <Space h={16} />

        <LBButton
          fullWidth
          disabled={!isValidForm}
          loading={form.submitting}
          type='submit'
          onClick={open}
        >
          {translate('commons.save-and-continue')}
        </LBButton>
      </form>
    </>
  );
};
